import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Tag } from 'antd';
import RiskSymbolsLegends from '@client/components/ui/RiskSymbolsLegends';

/**
 * Filter label mappings for translation
 * @type {Object<string, string>}
 */
const FILTER_LABELS = {
  names: 'active-substance|filters.Active substance name',
  cas_numbers: 'active-substance|filters.CAS number',
  cultures: 'active-substance|filters.Cultures',
  activity_sectors: 'active-substance|filters.Activity sector',
  cultures_practices: 'active-substance|filters.Cultural practices',
  pesticide_groups: 'active-substance|filters.Pesticide groups',
  pesticide_categories: 'active-substance|filters.Pesticide categories',
  pesticide_types: 'active-substance|filters.Pesticide types',
  chemical_families: 'active-substance|filters.Chemical families',
  // Health risk filters
  acute_toxicity: 'active-substance|filters.Acute toxicity',
  long_term_effects: 'active-substance|filters.Long term effects',
  // Environment risk filters
  fish_or_daphnia: 'active-substance|filters.Fish or daphnia',
  birds: 'active-substance|filters.Birds',
  bees: 'active-substance|filters.Bees',
  persistence: 'active-substance|filters.Persistence',
  leaching_potential: 'active-substance|filters.Leaching potential',
};

/**
 * Transforms filters object into active filter display format
 * @param {Object} filters - Current filter state
 * @param {Function} getFilterLabel - Function to get display labels
 * @param {Function} t - Translation function
 * @returns {Array<Object>} Array of active filter objects
 */
const getActiveFilters = (filters, getFilterLabel, t) => {
  return Object.entries(filters || {})
    .filter(([key, value]) => {
      return (
        Array.isArray(value) &&
        value.length > 0 &&
        key !== 'page' &&
        key !== 'size'
      );
    })
    .map(([key, value]) => ({
      key,
      label: FILTER_LABELS[key] ? t(FILTER_LABELS[key]) : key,
      values: value.map((v) => ({
        id: v,
        label: getFilterLabel(key, v),
      })).filter((item) => item.label),
    }))
    .filter((filter) => filter.values.length > 0);
};

/**
 * Legend section component
 * @param {Object} props - Component props
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} Legend section
 */
const LegendSection = ({ t }) => (
  <div className="flex justify-end mb-8">
    <div className="flex rounded-2xl bg-gray-50 border border-gray-200 px-6 py-2">
      <RiskSymbolsLegends title={t('active-substance|Risk symbols :')} />
    </div>
  </div>
);

/**
 * Active filters display section
 * @param {Object} props - Component props
 * @param {Array} props.activeFilters - List of active filters
 * @param {Function} props.onResetFilters - Reset filters callback
 * @param {Function} props.onRemoveFilter - Remove single filter callback
 * @param {Function} props.t - Translation function
 * @returns {JSX.Element} Active filters section
 */
const ActiveFiltersSection = ({ activeFilters, onResetFilters, onRemoveFilter, t, hasFilters }) => {
  const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
  
  const validFilters = activeFilters.map((filter) => ({
    ...filter,
    values: filter.values.filter((value) => value.label && !isUUID(value.label)),
  })).filter((filter) => filter.values.length > 0);

  const filterCount = validFilters.reduce((sum, filter) => sum + filter.values.length, 0);

  return (
    <div className="bg-green-900 text-lg p-6 rounded-2xl mb-8">
      <div className="flex justify-between items-center">
        <h3 className="text-white text-lg font-medium m-0">
          {t('common|Active filters')} {filterCount > 0 && `(${filterCount})`}
        </h3>
        <div className="flex gap-2">
          <Button size="large" onClick={onResetFilters}>
            {t('common|Reset')}
          </Button>
        </div>
      </div>
      {validFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {validFilters.map((filter) =>
            filter.values.map((value) => (
              <Tag
                key={`${filter.key}-${value.id}`}
                closable
                onClose={() => onRemoveFilter(filter.key, value.id)}
              >
                <strong>{filter.label}:</strong> {value.label}
              </Tag>
            ))
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main recap component that displays active filters with options to reset or share results
 * Works with nuqs for automatic URL synchronization
 * @param {Object} props - Component props
 * @param {Object} props.filters - Current filter state
 * @param {Function} props.onResetFilters - Callback to reset all filters
 * @param {Function} props.onRemoveFilter - Callback to remove a single filter value
 * @param {Function} props.getFilterLabel - Function to get display label for filter values
 * @returns {JSX.Element} Recap component with filters and legend
 */
const Recap = ({ filters, onResetFilters, onRemoveFilter, getFilterLabel }) => {
  const { t } = useTranslation();

  const activeFilters = getActiveFilters(filters, getFilterLabel, t);
  
  const hasAnyFilters = Object.entries(filters || {}).some(
    ([key, value]) => 
      Array.isArray(value) && 
      value.length > 0 && 
      key !== 'page' && 
      key !== 'size'
  );

  if (!hasAnyFilters) {
    return <LegendSection t={t} />;
  }

  const isUUID = (str) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);
  
  const validFilters = activeFilters.map((filter) => ({
    ...filter,
    values: filter.values.filter((value) => value.label && !isUUID(value.label)),
  })).filter((filter) => filter.values.length > 0);

  const hasValidLabels = validFilters.length > 0;

  return (
    <>
      {hasValidLabels ? (
        <ActiveFiltersSection
          activeFilters={activeFilters}
          onResetFilters={onResetFilters}
          onRemoveFilter={onRemoveFilter}
          hasFilters={hasAnyFilters}
          t={t}
        />
      ) : (
        <div className="bg-green-900 text-lg p-6 rounded-2xl mb-8">
          <div className="flex justify-between items-center">
            <h3 className="text-white text-lg font-medium m-0">
              {t('common|Active filters')}
            </h3>
            <div className="flex gap-2">
              <Button size="large" onClick={onResetFilters}>
                {t('common|Reset')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <LegendSection t={t} />
    </>
  );
};

export default Recap;
